// Clearing the service worker's offline API cache on an auth change (Session AH Item 5). The
// engineer diary is served stale-while-revalidate from a per-URL cache that carries no auth context,
// so on a shared device one engineer could briefly see another's cached week. Clearing it whenever a
// new engineer signs in or any user signs out removes that window; an engineer's own cache otherwise
// persists across reloads so the diary stays readable offline.
//
// Session AEK extends this to the browsable site and lift history (Director decision D33), which
// is company-wide data held in IndexedDB and carries the same shared-device exposure. It is
// cleared through the same single call, so every existing auth-change site (engineer sign-in, any
// sign-out) picks it up and no future one can forget. The engineer's OWN queued work (the
// completion, photo and message outboxes) is deliberately never cleared here: that is their work,
// it is not yet on the server, and it must survive to sync later.
//
// Session ASB (Director decision D-SEC-6) makes this the one "wipe every cached copy of SERVER data"
// call, for every way a session ends: a sign-out the engineer presses, and (Item 3 of the same
// session) the automatic sign-out on a 401. It now also clears the in-memory GET request cache in
// api.ts, which is keyed by path and not by user, and until ASB was emptied only as a side effect of
// the logout POST succeeding (a failed POST on a flaky network left every cached GET, /auth/me
// included, readable to whoever signed in next for up to five minutes).
//
// WHAT IS DELIBERATELY KEPT, and must stay kept: the three outboxes (queued completions, photos and
// messages) and the job sheet drafts (IndexedDB `sheetDrafts`). They are the engineer's own unsent
// work, not server data: the session can end in the middle of a visit (a 401 underground), and
// wiping them would destroy a signed sheet at exactly the moment the engineer is sent to sign in
// again. db.ts gives sheetDrafts no clear() for that reason, and draftStore.ts says so too.
//
// IT NEVER THROWS, AND IT SAYS WHETHER IT FINISHED (Session ASB, fix round 4). Every step is tried whatever happened to
// the one before, and a failure never reaches the caller as an error: auth must never fail on a cache. But a wipe that
// did not finish must not be mistaken for one that did, so the result is true only when every step succeeded (a Cache
// API that is not there has nothing to clear, which is not a failure) and false when any step failed: typically an
// IndexedDB whose connection WebKit dropped while the phone sat in the background. A caller that records what is left
// on the device (auth.tsx: the kept-copies hints and their expiry) reads it, so a failed wipe is tried again.
import { OFFLINE_API_CACHE } from "../pwa/cacheNames";
import { clearRequestCache } from "./api";
import { clearHistoryCache } from "./offline/historyStore";
import { clearTimesheetCache } from "./offline/timesheetStore";
import { clearCachedJobs } from "./offline/db";
import { bumpWipeGeneration } from "./offline/wipeEpoch";

export async function clearOfflineApiCache(): Promise<boolean> {
  // Session ASB review (I6-6): FIRST, before anything is awaited, so a read already in flight cannot write its answer
  // back into IndexedDB after the wipe below (offline/wipeEpoch.ts).
  bumpWipeGeneration();
  let finished = true;
  try {
    // Session ASB: the in-memory GET cache first, so nothing below can be read from memory while the
    // stores are being emptied. Synchronous and cannot really fail, but guarded like the rest.
    clearRequestCache();
  } catch {
    // Best-effort: auth must never fail on a cache.
    finished = false;
  }
  try {
    if (typeof caches !== "undefined") await caches.delete(OFFLINE_API_CACHE);
  } catch {
    // Best-effort: the Cache API may be unavailable (no SW, private mode). Never block auth on it.
    finished = false;
  }
  try {
    await clearHistoryCache();
  } catch {
    // Best-effort for the same reasons: IndexedDB may be unavailable, and auth must not fail on it.
    finished = false;
  }
  try {
    // The cached timesheet is one named engineer's own hours (Session AEL), so on a shared
    // device it must not outlive their session any more than the history cache does. Cleared
    // here rather than at a sign-out button so every auth-change site picks it up, including
    // the case the comment above names: a previous user who never signed out.
    await clearTimesheetCache();
  } catch {
    // Best-effort for the same reasons.
    finished = false;
  }
  try {
    // The cached job list now carries each site's ACCESS DETAILS (door codes, key locations,
    // Session AFW), which the unguarded "Continue offline" view reads with no auth. On a shared
    // device it must not outlive the session -- cleared here so every auth-change site (sign-out,
    // a different engineer signing in) disposes it, exactly as the history + timesheet caches are.
    await clearCachedJobs();
  } catch {
    // Best-effort for the same reasons; the outboxes and sheet drafts (the engineer's own unsent
    // work) are deliberately never cleared (see the header).
    finished = false;
  }
  return finished;
}
